import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GridModule, GridDataResult } from '@progress/kendo-angular-grid';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { IndicatorsModule } from '@progress/kendo-angular-indicators';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import { BomNode, BomLineItem, BomQueryOptions, PartType } from '../../models/file-vault.models';
import { LifecycleBadgeComponent } from '../lifecycle-badge/lifecycle-badge.component';

export type BomViewMode = 'tree' | 'flat';

export interface BomTreeSelectionEvent {
  node: BomLineItem;
  partId: string;
  partNumber: string;
}

@Component({
  selector: 'fv-bom-tree',
  standalone: true,
  template: `
    <div class="bom-tree-container">
      <!-- Toolbar -->
      <div class="toolbar" *ngIf="showToolbar">
        <div class="toolbar-left">
          <span class="title" *ngIf="rootPartNumber">BOM: {{ rootPartNumber }}</span>
        </div>
        <div class="toolbar-right">
          <div class="view-toggle">
            <button class="toggle-btn" [class.active]="viewMode === 'tree'" (click)="setViewMode('tree')">
              Indented
            </button>
            <button class="toggle-btn" [class.active]="viewMode === 'flat'" (click)="setViewMode('flat')">
              Flat
            </button>
          </div>
          <button kendoButton [look]="'flat'" (click)="refresh()" title="Refresh">
            Refresh
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div class="loading" *ngIf="isLoading">
        <kendo-loader [type]="'infinite-spinner'" [size]="'medium'"></kendo-loader>
        <span>Loading BOM...</span>
      </div>

      <!-- Empty State -->
      <div class="empty" *ngIf="!isLoading && bomData.length === 0">
        <div class="empty-icon">📦</div>
        <div class="empty-text">No BOM structure found</div>
        <div class="empty-hint" *ngIf="partId">This part has no child components</div>
      </div>

      <!-- BOM Grid -->
      <kendo-grid
        *ngIf="!isLoading && bomData.length > 0"
        [data]="gridData"
        [selectable]="{ mode: 'single' }"
        [sortable]="true"
        [pageable]="bomData.length > pageSize"
        [pageSize]="pageSize"
        (selectionChange)="onSelectionChange($event)"
        class="bom-grid">

        <kendo-grid-column field="level" title="Lvl" [width]="50" *ngIf="viewMode === 'tree'">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.level }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="findNumber" title="Find #" [width]="80">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.findNumber || '-' }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="partNumber" title="Part Number" [width]="200">
          <ng-template kendoGridCellTemplate let-dataItem>
            <div class="part-cell" [style.paddingLeft.px]="viewMode === 'tree' ? dataItem.level * 16 : 0">
              <span class="part-icon">{{ getPartIcon(dataItem) }}</span>
              <span class="part-number">{{ dataItem.partNumber || '-' }}</span>
            </div>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="partName" title="Name" [width]="250">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.partName || '-' }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="revision" title="Rev" [width]="60">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.revision || '-' }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="quantity" title="Qty" [width]="80">
          <ng-template kendoGridCellTemplate let-dataItem>
            <span class="quantity">{{ dataItem.quantity }}</span>
            <span class="uom" *ngIf="dataItem.unitOfMeasure">
              {{ dataItem.unitOfMeasure }}
            </span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="extendedQuantity" title="Ext Qty" [width]="80" *ngIf="showExtendedQuantity">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.extendedQuantity }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="refDesignator" title="Ref Des" [width]="100" *ngIf="showRefDesignator">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.refDesignator || '-' }}
          </ng-template>
        </kendo-grid-column>

      </kendo-grid>

      <!-- Summary -->
      <div class="summary" *ngIf="!isLoading && bomData.length > 0">
        <span class="item-count">{{ bomData.length }} item(s)</span>
        <span class="level-count" *ngIf="maxLevel > 0">{{ maxLevel + 1 }} level(s)</span>
      </div>
    </div>
  `,
  styles: [`
    .bom-tree-container {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    .toolbar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 12px;
      background: var(--fv-surface-hover);
      border-bottom: 1px solid var(--fv-border);
    }

    .toolbar-left {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .toolbar-right {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .title {
      font-weight: 600;
      font-size: 14px;
    }

    .view-toggle {
      display: flex;
      gap: 2px;
    }

    .toggle-btn {
      padding: 4px 10px;
      border: 1px solid var(--fv-border);
      background: var(--fv-surface);
      cursor: pointer;
      font-size: 12px;
      transition: all 0.2s;
    }

    .toggle-btn:first-child {
      border-radius: 4px 0 0 4px;
    }

    .toggle-btn:last-child {
      border-radius: 0 4px 4px 0;
    }

    .toggle-btn:hover {
      background: var(--fv-surface-hover);
    }

    .toggle-btn.active {
      background: var(--fv-text-accent);
      color: white;
      border-color: var(--fv-text-accent);
    }

    .loading {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 48px;
      gap: 12px;
      color: var(--fv-text-secondary);
    }

    .empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 48px;
      color: var(--fv-text-secondary);
    }

    .empty-icon {
      font-size: 48px;
      margin-bottom: 12px;
    }

    .empty-text {
      font-size: 16px;
      font-weight: 500;
    }

    .empty-hint {
      font-size: 13px;
      color: var(--fv-text-muted);
      margin-top: 4px;
    }

    .bom-grid {
      flex: 1;
      font-size: 13px;
    }

    .part-cell {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .part-icon {
      font-size: 14px;
    }

    .part-number {
      font-family: monospace;
      font-weight: 500;
    }

    .quantity {
      font-weight: 500;
    }

    .uom {
      font-size: 11px;
      color: var(--fv-text-secondary);
      margin-left: 2px;
    }

    .summary {
      display: flex;
      gap: 16px;
      padding: 8px 12px;
      background: var(--fv-surface-alt);
      border-top: 1px solid var(--fv-border);
      font-size: 12px;
      color: var(--fv-text-secondary);
    }
  `],
  imports: [CommonModule, GridModule, ButtonsModule, IndicatorsModule, LifecycleBadgeComponent]
})
export class BomTreeComponent implements OnInit, OnChanges {
  private api = inject(FileVaultApiService);

  /** Part ID to load BOM for */
  @Input() partId?: string;

  /** Part number to load BOM for (resolved to Part ID) */
  @Input() partNumber?: string;

  /** Part version ID (for version-specific BOM) */
  @Input() versionId?: string;

  /** Root part number for display */
  @Input() rootPartNumber?: string;

  /** Maximum depth to load */
  @Input() maxDepth = 10;

  /** Show toolbar */
  @Input() showToolbar = true;

  /** Show extended quantity column */
  @Input() showExtendedQuantity = true;

  /** Show reference designator column */
  @Input() showRefDesignator = false;

  /** Include inactive relationships */
  @Input() includeInactive = false;

  /** Effective date filter */
  @Input() effectiveDate?: Date;

  /** Page size for grid */
  @Input() pageSize = 50;

  /** View mode */
  @Input() viewMode: BomViewMode = 'tree';

  /** Emitted when a node is selected */
  @Output() nodeSelected = new EventEmitter<BomTreeSelectionEvent>();

  bomData: BomLineItem[] = [];
  isLoading = false;
  private resolvedPartId?: string;

  get gridData(): GridDataResult {
    return {
      data: this.bomData,
      total: this.bomData.length
    };
  }

  get maxLevel(): number {
    return Math.max(0, ...this.bomData.map(item => item.level));
  }

  ngOnInit(): void {
    this.loadBom();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['partId'] || changes['partNumber'] || changes['versionId']) && !changes['partId']?.firstChange) {
      this.loadBom();
    }
  }

  async loadBom(): Promise<void> {
    if (!this.partId && !this.partNumber && !this.versionId) {
      this.bomData = [];
      return;
    }

    this.isLoading = true;

    try {
      const options: BomQueryOptions = {
        maxDepth: this.maxDepth,
        includeInactive: this.includeInactive,
        effectiveDate: this.effectiveDate
      };

      const currentPartId = await this.getCurrentPartId();
      if (!currentPartId && !this.versionId) {
        this.bomData = [];
        return;
      }

      // Use flattened BOM which gives us the indented structure
      const items = await this.api.getFlattenedBom(currentPartId!, options).toPromise() ?? [];
      this.bomData = items;
    } catch (error) {
      console.error('Failed to load BOM', error);
      this.bomData = [];
    } finally {
      this.isLoading = false;
    }
  }

  onSelectionChange(event: any): void {
    if (event.selectedRows?.length > 0) {
      const item = event.selectedRows[0].dataItem as BomLineItem;
      this.nodeSelected.emit({
        node: item,
        partId: item.partId,
        partNumber: item.partNumber
      });
    }
  }

  setViewMode(mode: BomViewMode): void {
    this.viewMode = mode;
  }

  refresh(): void {
    this.loadBom();
  }

  private async getCurrentPartId(): Promise<string | undefined> {
    if (this.partId) {
      this.resolvedPartId = this.partId;
      return this.partId;
    }

    if (this.resolvedPartId && this.partNumber === undefined) {
      return this.resolvedPartId;
    }

    if (!this.partNumber) return undefined;

    try {
      const part = await this.api.getPartByNumber(this.partNumber).toPromise();
      if (part?.id) {
        this.resolvedPartId = part.id;
        if (!this.rootPartNumber) {
          this.rootPartNumber = part.partNumber;
        }
        return part.id;
      }
    } catch (error) {
      console.error('Failed to resolve part number', error);
    }

    return undefined;
  }

  getPartIcon(item: BomLineItem): string {
    // Assemblies typically have children (level < max)
    // This is a simplified heuristic
    if (item.level === 0) return '📦'; // Top-level assembly
    return '🔧'; // Component
  }
}
